import {
    useWindowDimensions,
    TouchableOpacity,
    Image,
    View
} from "react-native";
import type CompanyCardProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme,
    Sticker,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../../../navigation/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronRightIcon
} from "lucide-react-native";

const getInitials = (title: string) => {
    const words = title.trim().split(" ");
    if(words.length >= 2 && words[0] && words[1]) {
        return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
    }
    return title.slice(0, 2).toUpperCase();
};

const CompanyCard = ({
    sectorMap,
    company,
    index
}: CompanyCardProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 992;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const initials = getInitials(company.title);
    const companyKey = company._id || `company-${index}`;

    return <TouchableOpacity
        style={[
            stylesheet.tableRow,
            {
                padding: isMobile ? spaces.spacingMd : spaces.spacingLg,
                alignItems: isMobile ? "flex-start" : "center",
                backgroundColor: colors.project.brandWhite,
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? spaces.spacingMd : 0,
                marginBottom: spaces.spacingMd,
                borderRadius: radiuses.md
            }
        ]}
        onPress={() => {
            navigation.navigate("CompanyDetail", {
                companyID: company._id
            });
        }}
        activeOpacity={0.7}
    >
        <View
            style={[
                stylesheet.colCompany,
                {
                    width: isMobile ? "100%" : undefined,
                    gap: spaces.spacingMd
                }
            ]}
        >
            {
                company.companyPhotoPath ?
                    <Image
                        style={[
                            stylesheet.avatarImage as never,
                            {
                                borderRadius: radiuses.full
                            }
                        ]}
                        source={{
                            uri: company.companyPhotoPath
                        }}
                        resizeMode="cover"
                    />
                :
                    <View
                        style={[
                            stylesheet.avatarPlaceholder,
                            {
                                backgroundColor: colors.project.brandBorder,
                                borderRadius: radiuses.full
                            }
                        ]}
                    >
                        <Text
                            style={[
                                stylesheet.avatarText,
                                {
                                    color: colors.project.gold
                                }
                            ]}
                            variant="titleMediumSize"
                        >
                            {initials}
                        </Text>
                    </View>
            }
            <View
                style={stylesheet.companyTitleContainer}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {company.title}
                </Text>
            </View>
        </View>
        <View
            style={[
                stylesheet.colContact,
                {
                    width: isMobile ? "100%" : undefined,
                    paddingRight: spaces.spacingMd,
                    gap: spaces.spacingXs
                }
            ]}
        >
            <Text
                variant="bodyMediumSize"
            >
                {company.mail || "-"}
            </Text>
            <Text
                style={[
                    stylesheet.contactPhoneText,
                    {
                        color: colors.content.text.mid,
                        marginTop: spaces.spacingXs
                    }
                ]}
                variant="bodySmallSize"
            >
                {company.phoneNumber || "-"}
            </Text>
        </View>
        <View
            style={[
                stylesheet.colSector,
                {
                    width: isMobile ? "100%" : undefined,
                    gap: spaces.spacingXs,
                    flexDirection: "row",
                    flexWrap: "wrap"
                }
            ]}
        >
            {
                company.sectorIDs && company.sectorIDs.length > 0 ?
                    company.sectorIDs.map((sId, idx) => {
                        const sectorTitle = sectorMap[sId];
                        if(!sectorTitle) return null;
                        return <Sticker
                            key={`${companyKey}-sector-${idx}`}
                            spreadBehaviour="free"
                            customColor="primary"
                            title={sectorTitle}
                            size="small"
                        />;
                    })
                :
                    <Text
                        variant="bodyMediumSize"
                    >
                        -
                    </Text>
            }
        </View>
        <View
            style={[
                stylesheet.colActions,
                {
                    display: isMobile ? "none" : "flex"
                }
            ]}
        >
            <ChevronRightIcon
                color={colors.content.icon.mid}
                size={20}
            />
        </View>
    </TouchableOpacity>;
};

export default CompanyCard;
